import ServicePeriodEditor from '../company/corrections/ServicePeriodEditor'
import React, { useEffect, useMemo, useState } from 'react'
import { translateApiError } from '../../utils/errorMessages'
import api from '../../services/api'
import EntityHistory from '../../components/EntityHistory'
import ConfirmModal from '../../components/ConfirmModal'
import Icon from '../../components/Icon'
import { Button, Input, Select, FormGroup } from '../../components/ui'
import './Admin.css'

const formatPercentage = (value) => {
  if (value == null) return '-'
  return `${Number(value).toFixed(2)}%`
}

const formatDate = (value) => {
  if (!value) return 'без окончания'
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('ru-RU')
}

const formatMoney = (value) => {
  if (value == null || value === '') return '—'
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Number(value) || 0)
}

function BudgetEmployees() {
  const [activeTab, setActiveTab] = useState('employees')
  const [groups, setGroups] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [employees, setEmployees] = useState([])
  const [teamOptions, setTeamOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [periodForm, setPeriodForm] = useState({ valid_from: '', valid_to: '' })
  const [memberModal, setMemberModal] = useState(null)
  const [memberForm, setMemberForm] = useState({ employee_id: '', team_id: '', team: '', base_percentage: '' })
  const [createModal, setCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState({ valid_from: '', valid_to: '' })
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  const selectedGroup = detail?.group || groups.find((g) => g.id === selectedGroupId)
  const grouped = detail?.grouped || []
  const total = detail?.total || 0

  useEffect(() => {
    fetchInitial()
  }, [])

  useEffect(() => {
    if (!selectedGroupId) return
    fetchGroupDetail(selectedGroupId)
  }, [selectedGroupId])

  const fetchInitial = async () => {
    setLoading(true)
    setError(null)
    try {
      const [groupsRes, employeesRes, teamsRes] = await Promise.all([
        api.get('/admin/budget/percentage-groups'),
        api.get('/admin/budget/employees-simple'),
        api.get('/admin/budget/team-options')
      ])
      const groupList = groupsRes.data?.groups || []
      setGroups(groupList)
      setEmployees(employeesRes.data?.employees || [])
      setTeamOptions(teamsRes.data?.team_options || [])
      const current = groupList.find((g) => g.is_current) || groupList[0]
      if (current) setSelectedGroupId(current.id)
    } catch (err) {
      console.error('Error fetching percentage groups:', err)
      setLoadError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setLoading(false)
    }
  }

  const fetchGroups = async () => {
    const res = await api.get('/admin/budget/percentage-groups')
    const groupList = res.data?.groups || []
    setGroups(groupList)
    if (!selectedGroupId && groupList.length > 0) {
      const current = groupList.find((g) => g.is_current) || groupList[0]
      setSelectedGroupId(current.id)
    }
    return groupList
  }

  const fetchGroupDetail = async (groupId) => {
    setDetailLoading(true)
    setError(null)
    try {
      const res = await api.get(`/admin/budget/percentage-groups/${groupId}`)
      setDetail(res.data)
      setPeriodForm({
        valid_from: res.data?.group?.valid_from || '',
        valid_to: res.data?.group?.valid_to || ''
      })
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setDetailLoading(false)
    }
  }

  const reloadSelected = async (nextDetail) => {
    await fetchGroups()
    if (nextDetail) {
      setDetail(nextDetail)
      setPeriodForm({
        valid_from: nextDetail.group?.valid_from || '',
        valid_to: nextDetail.group?.valid_to || ''
      })
    } else if (selectedGroupId) {
      await fetchGroupDetail(selectedGroupId)
    }
  }

  const periodChanged = useMemo(() => {
    if (!selectedGroup) return false
    return (periodForm.valid_from || '') !== (selectedGroup.valid_from || '') ||
      (periodForm.valid_to || '') !== (selectedGroup.valid_to || '')
  }, [periodForm, selectedGroup])

  const savePeriod = async () => {
    if (!selectedGroupId) return
    if (!periodForm.valid_from) {
      setError('Укажите дату начала периода')
      return
    }
    const recalc = window.confirm('Сохранить период и пересчитать связанные стадии и акты?')
    setSaving(true)
    setError(null)
    try {
      const res = await api.put(`/admin/budget/percentage-groups/${selectedGroupId}`, {
        valid_from: periodForm.valid_from,
        valid_to: periodForm.valid_to || null,
        recalc
      })
      await reloadSelected(res.data)
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const openCreate = () => {
    setCreateForm({ valid_from: '', valid_to: '' })
    setError(null)
    setCreateModal(true)
  }

  const createGroup = async () => {
    if (!createForm.valid_from) {
      setError('Укажите дату начала периода')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await api.post('/admin/budget/percentage-groups', {
        valid_from: createForm.valid_from,
        valid_to: createForm.valid_to || null,
        source_group_id: selectedGroupId
      })
      const groupId = res.data?.group?.id
      setCreateModal(false)
      await fetchGroups()
      if (groupId) {
        setSelectedGroupId(groupId)
        setDetail(res.data)
      }
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const teamOptionValue = (option) => option.id || option.team_id || option.value || option.label || ''

  const findTeamOption = (value) => teamOptions.find((option) => String(teamOptionValue(option)) === String(value))

  const openAddMember = (teamGroup = {}) => {
    setMemberForm({
      employee_id: '',
      team_id: teamGroup.team_id || '',
      team: teamGroup.team || '',
      base_percentage: '0'
    })
    setMemberModal({ type: 'add' })
    setError(null)
  }

  const openEditMember = (record, teamGroup = {}) => {
    setMemberForm({
      employee_id: record.employee_id,
      team_id: teamGroup.team_id || '',
      team: teamGroup.team || '',
      base_percentage: record.base_percentage != null ? String(record.base_percentage) : '0'
    })
    setMemberModal({ type: 'edit', record })
    setError(null)
  }

  const saveMember = async () => {
    if (!selectedGroupId || !memberModal) return
    const employee_id = (memberForm.employee_id || '').trim()
    const team = (memberForm.team || '').trim()
    const pct = memberForm.base_percentage === '' ? 0 : parseFloat(memberForm.base_percentage)
    if (!team) {
      setError('Укажите команду')
      return
    }
    if (!employee_id) {
      setError('Укажите сотрудника')
      return
    }
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setError('Процент от 0 до 100')
      return
    }
    const recalc = window.confirm('Сохранить процент и пересчитать связанные стадии и акты?')
    setSaving(true)
    setError(null)
    try {
      let res
      if (memberModal.type === 'add' || !memberModal.record?.member_id) {
        res = await api.post(`/admin/budget/percentage-groups/${selectedGroupId}/members`, {
          employee_id,
          team_id: memberForm.team_id || undefined,
          team,
          base_percentage: pct,
          recalc
        })
      } else {
        res = await api.put(`/admin/budget/percentage-groups/${selectedGroupId}/members/${memberModal.record.member_id}`, {
          base_percentage: pct,
          recalc
        })
      }
      setMemberModal(null)
      await reloadSelected(res.data)
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedGroupId || !deleteTarget) return
    setDeleteError(null)
    try {
      const res = await api.delete(`/admin/budget/percentage-groups/${selectedGroupId}/members/${deleteTarget.member_id}?recalc=1`)
      setDeleteTarget(null)
      await reloadSelected(res.data)
    } catch (err) {
      setDeleteError(translateApiError(err.response?.data?.error || err.message))
    }
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="admin-page">
      {(loadError || error) && (
        <p className="modal-error" style={{ marginBottom: 'var(--spacing-md)' }}>
          {loadError || error}
        </p>
      )}

      <div className="section-card">
        <div className="section-card-header">
          <h2>Базовые проценты сотрудников</h2>
          <Button variant="primary" onClick={openCreate}>+ Создать период</Button>
        </div>
        {groups.length === 0 ? (
          <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>Нет периодов</p>
        ) : (
          <div className="percentage-groups-list">
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                className={`percentage-group-row ${selectedGroupId === group.id ? 'active' : ''}`}
                onClick={() => setSelectedGroupId(group.id)}
              >
                <span className="percentage-group-row-main">
                  <strong>{formatDate(group.valid_from)} — {formatDate(group.valid_to)}</strong>
                  {group.is_current && <span className="status-badge status-success">Действующая</span>}
                </span>
                <span className="percentage-group-row-meta">
                  {group.stage_count || 0} стадий · {group.act_count || 0} актов
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {detailLoading ? (
        <div className="loading">Загрузка...</div>
      ) : selectedGroup ? (
        <div className="section-card">
          <div className="section-card-header">
            <div>
              <h2>Период {formatDate(selectedGroup.valid_from)} — {formatDate(selectedGroup.valid_to)}</h2>
              <p className="admin-subtitle" style={{ margin: 'var(--spacing-xs) 0 0 0' }}>
                Всего {total} {total === 1 ? 'запись' : total < 5 ? 'записи' : 'записей'}
              </p>
            </div>
            {selectedGroup.is_current && <span className="status-badge status-success">Действующая</span>}
          </div>

          <div className="percentage-period-form">
            <FormGroup label="С">
              <Input
                type="date"
                value={periodForm.valid_from}
                onChange={(e) => setPeriodForm((f) => ({ ...f, valid_from: e.target.value }))}
              />
            </FormGroup>
            <FormGroup label="По включительно">
              <Input
                type="date"
                value={periodForm.valid_to || ''}
                onChange={(e) => setPeriodForm((f) => ({ ...f, valid_to: e.target.value }))}
              />
            </FormGroup>
            <Button variant="secondary" onClick={savePeriod} disabled={saving || !periodChanged}>
              Сохранить период
            </Button>
          </div>

          <div className="cw-tabs"><button className={activeTab==='services'?'active':''} onClick={()=>setActiveTab('services')}>Подразделения обслуживания</button><button className={activeTab==='employees'?'active':''} onClick={()=>setActiveTab('employees')}>Сотрудники</button></div>
          {activeTab==='services' ? <ServicePeriodEditor key={selectedGroupId} groupId={selectedGroupId}/> : <>
          {grouped.length === 0 ? (
            <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>Нет сотрудников в группе</p>
          ) : (
            grouped.map((deptGroup) => (
              <div key={deptGroup.department} className="budget-employees-dept-block">
                <h3 className="budget-employees-dept-title">{deptGroup.department}</h3>
                {(deptGroup.teams || []).map((teamGroup) => {
                  const sumInvalid = Math.abs((teamGroup.sum_percentage || 0) - 100) > 0.01
                  return (
                    <div key={teamGroup.team_id || teamGroup.team} className="budget-employees-team-card">
                      <div className="budget-employees-team-header">
                        <strong>{teamGroup.team}</strong>
                        <span className={sumInvalid ? 'modal-error' : 'status-badge status-success'} style={{ marginLeft: 'var(--spacing-sm)', fontSize: '0.9em' }}>
                          Сумма: {formatPercentage(teamGroup.sum_percentage)}
                        </span>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => openAddMember(teamGroup)}
                          style={{ marginLeft: 'auto' }}
                        >
                          + Добавить
                        </Button>
                      </div>
                      <ul className="budget-employees-list">
                        {(teamGroup.employees || []).map((emp) => (
                          <li key={emp.member_id || `${teamGroup.team_id || teamGroup.team}-${emp.employee_id}`} className="budget-employees-row">
                            <span>{emp.full_name || '—'}</span>
                            <span>{formatPercentage(emp.base_percentage)}</span>
                            <span>
                              <Button
                                variant="ghost"
                                className="icon-button"
                                onClick={() => openEditMember(emp, teamGroup)}
                                title="Изменить процент"
                                aria-label="Изменить"
                              >
                                <Icon name="edit" size={16} />
                              </Button>
                              {emp.member_id ? (
                                <Button
                                  variant="ghost"
                                  className="icon-button"
                                  onClick={() =>
                                    setDeleteTarget({
                                      member_id: emp.member_id,
                                      label: `${emp.full_name} в ${teamGroup.team}`
                                    })
                                  }
                                  title="Удалить"
                                  aria-label="Удалить"
                                >
                                  <Icon name="trash" size={16} />
                                </Button>
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            ))
          )}

          </>}
          <div className="percentage-group-linked">
            <div>
              <h3>Стадии ({detail?.stages?.length || 0})</h3>
              {(detail?.stages || []).length === 0 ? (
                <p className="admin-subtitle">Нет стадий с этой группой.</p>
              ) : (
                <ul className="percentage-linked-list">
                  {detail.stages.map((stage) => (
                    <li key={stage.id}>
                      <span>{stage.contract_number ? `${stage.contract_number} — ` : ''}{stage.type || stage.id}</span>
                      <span>{formatMoney(stage.cost_after_tax || stage.cost)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3>Акты ({detail?.acts?.length || 0})</h3>
              {(detail?.acts || []).length === 0 ? (
                <p className="admin-subtitle">Нет актов в периоде.</p>
              ) : (
                <ul className="percentage-linked-list">
                  {detail.acts.map((act) => (
                    <li key={act.id}>
                      <span>{formatDate(act.issue_date)} · {act.contract_number || '—'} · {act.number || act.id}</span>
                      <span>{formatMoney(act.amount_after_tax || act.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <EntityHistory entityType="employee_percentage_group_members" title="История изменений (базовые проценты)" />

      {createModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => !saving && setCreateModal(false)}>
          <div className="modal-content" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <h3>Создать период</h3>
            <FormGroup label="С">
              <Input type="date" value={createForm.valid_from} onChange={(e) => setCreateForm((f) => ({ ...f, valid_from: e.target.value }))} />
            </FormGroup>
            <FormGroup label="По включительно">
              <Input type="date" value={createForm.valid_to} onChange={(e) => setCreateForm((f) => ({ ...f, valid_to: e.target.value }))} />
            </FormGroup>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => !saving && setCreateModal(false)} disabled={saving}>Отмена</Button>
              <Button variant="primary" onClick={createGroup} disabled={saving}>{saving ? 'Создание...' : 'Создать'}</Button>
            </div>
          </div>
        </div>
      )}

      {memberModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => !saving && setMemberModal(null)}>
          <div className="modal-content" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <h3>{memberModal.type === 'add' ? 'Добавить сотрудника' : 'Изменить процент'}</h3>
            <FormGroup label="Команда">
              <Select
                value={memberForm.team_id || memberForm.team}
                aria-label="Команда"
                onChange={(e) => {
                  const value = e.target.value
                  const option = findTeamOption(value)
                  setMemberForm((f) => ({
                    ...f,
                    team_id: option?.id || option?.team_id || '',
                    team: option?.team_name || option?.value || option?.label || value
                  }))
                }}
                disabled={memberModal.type === 'edit'}
              >
                <option value="">— не выбрана —</option>
                {[
                  ...(memberForm.team && !teamOptions.some((o) => String(teamOptionValue(o)) === String(memberForm.team_id || memberForm.team))
                    ? [{ value: memberForm.team, label: memberForm.team }]
                    : []),
                  ...teamOptions
                ].map((o) => (
                  <option key={teamOptionValue(o)} value={teamOptionValue(o)}>{o.label || o.value}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Сотрудник">
              <Select
                value={memberForm.employee_id}
                aria-label="Сотрудник"
                onChange={(e) => setMemberForm((f) => ({ ...f, employee_id: e.target.value }))}
                disabled={memberModal.type === 'edit'}
              >
                <option value="">— не выбран —</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name || employee.id}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Базовый процент (%)">
              <Input
                type="number"
                aria-label="Базовый процент (%)"
                min={0}
                max={100}
                step={0.001}
                value={memberForm.base_percentage}
                onChange={(e) => setMemberForm((f) => ({ ...f, base_percentage: e.target.value }))}
              />
            </FormGroup>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => !saving && setMemberModal(null)} disabled={saving}>Отмена</Button>
              <Button variant="primary" onClick={saveMember} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</Button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Удалить привязку?"
          message={<p>Удалить базовый процент: {deleteTarget.label}? Связанные стадии и акты будут пересчитаны.</p>}
          confirmLabel="Удалить"
          onConfirm={handleDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteError(null) }}
          error={deleteError}
        />
      )}
    </div>
  )
}

export default BudgetEmployees
